import { isLocalMode } from './storageMode';

// ---------------------------------------------------------------------------
// Drop / paste extraction helpers
// ---------------------------------------------------------------------------

function looksLikeImageUrl(url) {
  if (typeof url !== 'string') return false;
  return (
    /\.(jpe?g|png|gif|webp|avif|svg|bmp)(\?.*)?$/i.test(url) ||
    /pinimg\.com|googleusercontent\.com|unsplash\.com|imgur\.com/i.test(url)
  );
}

/**
 * Pull the best image URL out of a dataTransfer object.
 * Returns a string URL or null — never throws.
 */
export function extractDropUrl(dataTransfer) {
  if (!dataTransfer) return null;

  // 1. text/uri-list — most reliable for standard image drags
  try {
    const uriList = dataTransfer.getData('text/uri-list');
    if (uriList) {
      const lines = uriList
        .split(/\r?\n/)
        .map(l => l.trim())
        .filter(l => l && !l.startsWith('#'));
      // Prefer a line that already looks like an image
      const imageUrl = lines.find(looksLikeImageUrl) ?? lines[0];
      if (imageUrl) return imageUrl;
    }
  } catch { /* sandboxed iframe may throw — swallow */ }

  // 2. text/html — parse <img src> and data-src (lazy-load pattern)
  try {
    const html = dataTransfer.getData('text/html');
    if (html) {
      // Scan every src / data-src attribute for the first http URL
      for (const match of html.matchAll(/(?:data-src|src)=["']([^"']+)["']/gi)) {
        if (match[1].startsWith('http')) return match[1];
      }
      // Fallback: any bare http URL inside the HTML that looks image-like
      for (const match of html.matchAll(/https?:\/\/[^\s"'<>]+/gi)) {
        if (looksLikeImageUrl(match[0])) return match[0];
      }
    }
  } catch { /* swallow */ }

  // 3. text/plain — last resort
  try {
    const plain = dataTransfer.getData('text/plain')?.trim();
    if (plain?.startsWith('http')) return plain;
  } catch { /* swallow */ }

  return null;
}

/**
 * Extract an image Blob from a ClipboardEvent's clipboardData.
 * Returns { blob, url } where exactly one will be non-null, or null if nothing found.
 */
export function extractClipboardImage(clipboardData) {
  if (!clipboardData) return null;

  const items = Array.from(clipboardData.items ?? []);

  const imageItem = items.find(i => i.kind === 'file' && i.type.startsWith('image/'));
  if (imageItem) return { blob: imageItem.getAsFile(), url: null };

  const textItem = items.find(i => i.kind === 'string' && i.type === 'text/plain');
  if (textItem) {
    // getAsString is async — caller must handle this as a Promise
    return {
      blob: null,
      urlPromise: new Promise(resolve => textItem.getAsString(resolve)),
    };
  }

  return null;
}

// ---------------------------------------------------------------------------

/**
 * Loads an image just to read its natural pixel dimensions.
 * Returns null (rather than throwing) if the image can't be decoded.
 */
export function getImageNaturalSize(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

// Canvas items stay legible at any zoom level, so dropped images are clamped
// to this range (longest side) rather than placed at raw pixel dimensions.
const DROP_SIZE_MIN = 160;
const DROP_SIZE_MAX = 420;

/**
 * Picks a canvas size for a newly-dropped image: same aspect ratio as the
 * source, scaled only enough to keep the longest side within
 * [DROP_SIZE_MIN, DROP_SIZE_MAX] so most images land at a similar footprint.
 */
export function computeDropSize(naturalWidth, naturalHeight) {
  const width = naturalWidth || 300;
  const height = naturalHeight || 200;
  const longest = Math.max(width, height);

  let scale = 1;
  if (longest > DROP_SIZE_MAX) scale = DROP_SIZE_MAX / longest;
  else if (longest < DROP_SIZE_MIN) scale = DROP_SIZE_MIN / longest;

  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
}

/**
 * Normalizes stored asset strings at runtime.
 * Strips hardcoded origins so relative "/uploads/..." paths render correctly
 * regardless of which host they were saved from, and prefixes the current
 * API_BASE so <img src> resolves against the right backend.
 */
export async function resolveImageUrl(srcString) {
  if (!srcString) return '';

  let cleanSrc = srcString;

  if (typeof cleanSrc === 'string' && cleanSrc.includes('/uploads/')) {
    const uploadsIndex = cleanSrc.indexOf('/uploads/');
    cleanSrc = cleanSrc.substring(uploadsIndex);
  }

  if (typeof cleanSrc === 'string' && cleanSrc.startsWith('/uploads/')) {
    const API_BASE = process.env.REACT_APP_API_BASE || '';
    const cleanBase = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
    return `${cleanBase}${cleanSrc}`;
  }

  return cleanSrc;
}

// Offline-only fallback (see uploadImageFile/uploadImageToServer below) — the
// backend (sharp + R2) is the primary path and does this properly server-side.
export function toWebP(blob, quality = 0.65, maxWidth = 800) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/webp', quality));
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

// True only for a real network failure (offline, DNS, connection refused) —
// fetch() rejects with a TypeError in that case, as opposed to an HTTP error
// status which resolves normally.
function isNetworkFailure(err) {
  return err instanceof TypeError;
}

export async function fetchImageFromUrl(url) {
  if (isLocalMode()) {
    throw new Error("Image URLs aren't supported in local mode — save the image and drop the file instead.");
  }

  const token = localStorage.getItem('token');
  const API_BASE = process.env.REACT_APP_API_BASE || '';

  const res = await fetch(`${API_BASE}/api/fetch-image`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ url }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch image');
  }

  // Backend now returns { url } (disk path). Fall back to dataUrl for old responses.
  const data = await res.json();
  return data.url ?? data.dataUrl;
}

/**
 * Upload a file for use as a note-embedded image. Logged-in users go through
 * the backend (sharp downscale/compress + R2 storage), getting a 1200px/
 * quality-80 WebP under a public R2 URL. Local-storage-mode (no account)
 * never touches the server or R2 — everything stays on-device, so the image
 * is downscaled/encoded to WebP right here in the browser instead. Falls
 * back to the same in-browser conversion if the network is unreachable.
 * Returns { url, sizeBytes } — `url` is safe to use directly as <img src>.
 */
export async function uploadImageFile(file) {
  if (isLocalMode()) {
    return { url: await toWebP(file) };
  }

  const token = localStorage.getItem('token');
  const API_BASE = process.env.REACT_APP_API_BASE || '';

  const form = new FormData();
  form.append('image', file);

  try {
    const res = await fetch(`${API_BASE}/api/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Image upload failed');
    }

    return res.json(); // { url, sizeBytes }
  } catch (err) {
    if (!isNetworkFailure(err)) throw err;
    return { url: await toWebP(file) };
  }
}

// Same as uploadImageFile but returns the bare URL string, for callers that
// don't need sizeBytes.
export async function uploadImageToServer(file) {
  const { url } = await uploadImageFile(file);
  return url;
}